/**
 * The dashboard's filter row.
 *
 * One row, above everything it scopes. Every tile re-queries against the same
 * selection, so the numbers on a dashboard always agree with each other — a
 * per-chart filter would let two tiles disagree and look like a data bug.
 *
 * Which filters appear is derived from the charts on the dashboard rather than
 * configured: the backend catalogue says which parameters each query accepts,
 * so a dashboard of charts that take `platform` gets a store filter and one
 * that doesn't gets nothing. That keeps the control from promising a filter the
 * data cannot honour.
 */

import {
  DEFAULT_TIMEFRAME_DAYS,
  TIMEFRAME_PRESETS,
  rangeForDays,
  validRange,
} from '../timeframe';
import type { FilterOption } from '../types';

export type { FilterOption };

export interface FilterSpec {
  /** The query parameter this control sets. */
  param: string;
  label: string;
  options: FilterOption[];
  /** Shown in place of the control when there is nothing to choose from. */
  unavailable?: string;
}

/**
 * The two stores, by the values the API actually matches on.
 *
 * `itunes`, not `ios` — `ios` is accepted and returns nothing, which is the
 * worst kind of wrong value because it looks like an empty result rather than
 * a bad parameter.
 */
export const STORE_FILTER: FilterSpec = {
  param: 'platform',
  label: 'Store',
  options: [
    { value: 'android', label: 'Google Play' },
    { value: 'itunes', label: 'App Store' },
  ],
};

/** The viewer's chosen window: a rolling preset, or two dates they picked. */
export type TimeframeChoice =
  | { mode: 'preset'; days: number }
  | { mode: 'custom'; from: string; to: string };

interface TimeframeProps {
  value: TimeframeChoice;
  onChange: (next: TimeframeChoice) => void;
  /** Shown beside the control, e.g. when some tiles ignore the range. */
  note?: string;
}

const CUSTOM = 'custom';

const TimeframeControl = ({ value, onChange, note }: TimeframeProps) => {
  const days = value.mode === 'preset' ? value.days : null;
  // A saved default that is not one of the presets (say 45) still has to show
  // as itself rather than as a blank select.
  const listed = TIMEFRAME_PRESETS.some((p) => p.days === days);
  const invalid = value.mode === 'custom' && !validRange(value.from, value.to);

  return (
    <div className="filter filter--timeframe">
      <label className="filter">
        <span className="filter__label">Time frame</span>
        <select
          value={value.mode === 'custom' ? CUSTOM : String(value.days)}
          onChange={(e) => {
            if (e.target.value === CUSTOM) {
              const range = rangeForDays(value.mode === 'preset' ? value.days : DEFAULT_TIMEFRAME_DAYS);
              onChange({ mode: 'custom', ...range });
            } else {
              onChange({ mode: 'preset', days: Number(e.target.value) });
            }
          }}
        >
          {days !== null && !listed && <option value={days}>Last {days} days</option>}
          {TIMEFRAME_PRESETS.map((p) => (
            <option key={p.days} value={p.days}>
              {p.label}
            </option>
          ))}
          <option value={CUSTOM}>Custom range…</option>
        </select>
      </label>
      {value.mode === 'custom' && (
        <div className="filter__dates">
          <label>
            <span className="visually-hidden">From</span>
            <input
              type="date"
              value={value.from}
              max={value.to || undefined}
              onChange={(e) => {
                // A cleared box is ignored and a start after the end drags the
                // end along, so the range is never one the charts can't use.
                const from = e.target.value;
                if (from) onChange({ ...value, from, to: from > value.to ? from : value.to });
              }}
            />
          </label>
          <span aria-hidden="true">–</span>
          <label>
            <span className="visually-hidden">To</span>
            <input
              type="date"
              value={value.to}
              min={value.from || undefined}
              onChange={(e) => {
                const to = e.target.value;
                if (to) onChange({ ...value, to, from: to < value.from ? to : value.from });
              }}
            />
          </label>
        </div>
      )}
      {invalid && <span className="filter__note">Choose a start date on or before the end date.</span>}
      {note && <span className="filter__note">{note}</span>}
    </div>
  );
};

interface Props {
  filters: FilterSpec[];
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
  timeframe?: TimeframeProps;
}

export const FilterBar = ({ filters, value, onChange, timeframe }: Props) => {
  if (!filters.length && !timeframe) return null;

  return (
    <div className="filter-bar" role="group" aria-label="Filters">
      {filters.map((filter) => {
        const disabled = filter.options.length === 0;
        return (
          <label className="filter" key={filter.param}>
            <span className="filter__label">{filter.label}</span>
            <select
              value={value[filter.param] ?? ''}
              disabled={disabled}
              title={disabled ? filter.unavailable : undefined}
              onChange={(e) => {
                const next = { ...value };
                if (e.target.value) next[filter.param] = e.target.value;
                else delete next[filter.param];
                onChange(next);
              }}
            >
              <option value="">All</option>
              {filter.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {disabled && filter.unavailable && (
              <span className="filter__note">{filter.unavailable}</span>
            )}
          </label>
        );
      })}
      {timeframe && <TimeframeControl {...timeframe} />}
    </div>
  );
};
